import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import AdBanner from '../components/AdBanner';
import SignaturePad from '../components/SignaturePad';
import '../components/SignaturePad.css';
import { generateBillOfSalePDF, generateBlankBillOfSalePDF, downloadPDF } from '../utils/pdfGenerator';
import api from '../services/api';
import './DocumentForm.css';

const BillOfSale = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editDocument = location.state?.editDocument;
  const { isAuthenticated, isPremium } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sellerSignature, setSellerSignature] = useState(null);
  const [buyerSignature, setBuyerSignature] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Get template from URL query param or default to generic
  const urlTemplate = searchParams.get('template');
  const initialTemplate = ['generic', 'vehicle', 'electronics', 'equipment'].includes(urlTemplate) 
    ? urlTemplate 
    : 'generic';
  const [templateType, setTemplateType] = useState(initialTemplate);
  
  const [formData, setFormData] = useState({
    seller: {
      name: '',
      address: { street: '', city: '', state: '', zipCode: '' },
      phone: '',
      email: ''
    },
    buyer: {
      name: '',
      address: { street: '', city: '', state: '', zipCode: '' },
      phone: '',
      email: ''
    },
    item: {
      description: '',
      condition: 'good',
      serialNumber: '',
      make: '',
      model: '',
      year: '',
      color: '',
      additionalDetails: '',
      // Vehicle-specific fields
      vin: '',
      odometer: '',
      licensePlate: '',
      titleStatus: 'clean',
      // Electronics-specific fields
      imei: '',
      storageCapacity: '',
      // Equipment-specific fields
      accessories: ''
    },
    transaction: {
      salePrice: '',
      paymentMethod: 'cash',
      saleDate: new Date().toISOString().split('T')[0],
      location: ''
    },
    terms: {
      asIs: true,
      warranty: false,
      warrantyDetails: ''
    },
    platform: 'other',
    notes: '',
    templateType: 'generic'
  });

  // Pre-fill form if editing an existing document
  useEffect(() => {
    if (editDocument) {
      setIsEditing(true);
      setTemplateType(editDocument.templateType || 'generic');
      setFormData({
        seller: {
          name: editDocument.seller?.name || '',
          address: {
            street: editDocument.seller?.address?.street || '',
            city: editDocument.seller?.address?.city || '',
            state: editDocument.seller?.address?.state || '',
            zipCode: editDocument.seller?.address?.zipCode || ''
          },
          phone: editDocument.seller?.phone || '',
          email: editDocument.seller?.email || ''
        },
        buyer: {
          name: editDocument.buyer?.name || '',
          address: {
            street: editDocument.buyer?.address?.street || '',
            city: editDocument.buyer?.address?.city || '',
            state: editDocument.buyer?.address?.state || '',
            zipCode: editDocument.buyer?.address?.zipCode || ''
          },
          phone: editDocument.buyer?.phone || '',
          email: editDocument.buyer?.email || ''
        },
        item: {
          description: editDocument.item?.description || '',
          condition: editDocument.item?.condition || 'good',
          serialNumber: editDocument.item?.serialNumber || '',
          make: editDocument.item?.make || '',
          model: editDocument.item?.model || '',
          year: editDocument.item?.year || '',
          color: editDocument.item?.color || '',
          additionalDetails: editDocument.item?.additionalDetails || '',
          // Vehicle-specific
          vin: editDocument.item?.vin || '',
          odometer: editDocument.item?.odometer || '',
          licensePlate: editDocument.item?.licensePlate || '',
          titleStatus: editDocument.item?.titleStatus || 'clean',
          // Electronics-specific
          imei: editDocument.item?.imei || '',
          storageCapacity: editDocument.item?.storageCapacity || '',
          // Equipment-specific
          accessories: editDocument.item?.accessories || ''
        },
        transaction: {
          salePrice: editDocument.transaction?.salePrice || '',
          paymentMethod: editDocument.transaction?.paymentMethod || 'cash',
          saleDate: editDocument.transaction?.saleDate 
            ? new Date(editDocument.transaction.saleDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          location: editDocument.transaction?.location || ''
        },
        terms: {
          asIs: editDocument.terms?.asIs ?? true,
          warranty: editDocument.terms?.warranty ?? false,
          warrantyDetails: editDocument.terms?.warrantyDetails || ''
        },
        platform: editDocument.platform || 'other',
        notes: editDocument.notes || '',
        templateType: editDocument.templateType || 'generic'
      });
      
      // Restore signatures if they exist
      if (editDocument.sellerSignature) {
        setSellerSignature(editDocument.sellerSignature);
      }
      if (editDocument.buyerSignature) {
        setBuyerSignature(editDocument.buyerSignature);
      }
      
      toast.info('Editing saved document - changes will create a new version');
    }
  }, [editDocument]);

  // Update page title for SEO based on template
  useEffect(() => {
    const titles = {
      generic: 'Free Bill of Sale Generator | PrivateSold',
      vehicle: 'Free Vehicle Bill of Sale Generator | Car, Truck, Motorcycle | PrivateSold',
      electronics: 'Free Electronics Bill of Sale | Phone, Laptop, Gaming | PrivateSold',
      equipment: 'Free Equipment Bill of Sale Generator | Tools, Machinery | PrivateSold'
    };
    document.title = titles[templateType] || titles.generic;
    
    // Cleanup - reset title when leaving
    return () => {
      document.title = 'PrivateSold - Bill of Sale & Invoice Generator';
    };
  }, [templateType]);

  const handleTemplateChange = (newTemplate) => {
    setTemplateType(newTemplate);
    setFormData(prev => ({
      ...prev,
      templateType: newTemplate
    }));
  };

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: typeof prev[section] === 'object' 
        ? { ...prev[section], [field]: value }
        : value
    }));
  };

  const handleAddressChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        address: {
          ...prev[section].address,
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate emails if sendEmails is checked
    if (sendEmails && isPremium()) {
      if (!formData.seller.email || !formData.buyer.email) {
        toast.error('Please enter email addresses for both seller and buyer');
        return;
      }
    }
    
    setLoading(true);

    try {
      // Prepare data
      const billData = {
        ...formData,
        templateType,
        sellerSignature,
        buyerSignature,
        sellerSignedAt: sellerSignature ? new Date() : null,
        buyerSignedAt: buyerSignature ? new Date() : null,
        transaction: {
          ...formData.transaction,
          salePrice: parseFloat(formData.transaction.salePrice) || 0
        }
      };

      // Generate the PDF first (we'll need the document number)
      let documentNumber;
      
      // If premium user with emails, use the with-email endpoint
      if (isAuthenticated && isPremium() && sendEmails) {
        // Generate PDF to get base64 for email attachment
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 6).toUpperCase();
        documentNumber = `BOS-${timestamp}-${random}`;
        billData.documentNumber = documentNumber;
        
        const pdf = generateBillOfSalePDF(billData);
        const pdfBase64 = pdf.output('datauristring').split(',')[1]; // Get base64 part
        
        await api.post('/bills/with-email', {
          ...billData,
          sellerEmail: formData.seller.email,
          buyerEmail: formData.buyer.email,
          pdfBase64
        });
        
        // Download the PDF locally too
        downloadPDF(pdf, `bill-of-sale-${documentNumber}.pdf`);
        toast.success('Document saved and email copies sent!');
      } else if (isAuthenticated) {
        // Regular authenticated user (premium without emails, or free)
        const response = await api.post('/bills', billData);
        documentNumber = response.data.bill.documentNumber;
        billData.documentNumber = documentNumber;
        
        // Generate and download PDF
        const pdf = generateBillOfSalePDF(billData);
        downloadPDF(pdf, `bill-of-sale-${documentNumber}.pdf`);
        
        if (response.data.saved) {
          toast.success('Bill of sale saved to your account!');
        } else {
          toast.info('Upgrade to Premium to save documents', { autoClose: 4000 });
        }
      } else {
        // Generate temporary document number for non-authenticated
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 6).toUpperCase();
        documentNumber = `PS-${timestamp}-${random}`;
        billData.documentNumber = documentNumber;
        
        // Generate and download PDF
        const pdf = generateBillOfSalePDF(billData);
        downloadPDF(pdf, `bill-of-sale-${documentNumber}.pdf`);
        toast.success('PDF downloaded successfully!');
      }

      if (!isAuthenticated) {
        toast.info('Sign up to save your documents!', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error generating bill of sale:', error);
      toast.error('Error generating document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBlankTemplate = () => {
    const pdf = generateBlankBillOfSalePDF();
    downloadPDF(pdf, 'bill-of-sale-template.pdf');
    toast.success('Blank template downloaded!');
  };

  // Template config for SEO and display
  const templateConfig = {
    generic: {
      title: 'Bill of Sale Generator',
      subtitle: 'Create a professional bill of sale for your transaction',
      icon: '📄'
    },
    vehicle: {
      title: 'Vehicle Bill of Sale Generator',
      subtitle: 'Create a bill of sale for cars, trucks, motorcycles, boats, and other vehicles',
      icon: '🚗'
    },
    electronics: {
      title: 'Electronics Bill of Sale Generator',
      subtitle: 'Create a bill of sale for phones, laptops, gaming consoles, and electronics',
      icon: '📱'
    },
    equipment: {
      title: 'Equipment Bill of Sale Generator',
      subtitle: 'Create a bill of sale for tools, machinery, appliances, and equipment',
      icon: '🔧'
    }
  };

  const currentTemplate = templateConfig[templateType] || templateConfig.generic;

  return (
    <div className="document-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            {isEditing ? 'Edit Bill of Sale' : currentTemplate.title}
          </h1>
          <p className="page-subtitle">
            {isEditing 
              ? 'Make changes and generate a new PDF'
              : currentTemplate.subtitle
            }
          </p>
        </div>

        {/* Template Selector */}
        {!isEditing && (
          <div className="template-selector">
            <label className="template-label">Select Template Type:</label>
            <div className="template-options">
              <button
                type="button"
                className={`template-option ${templateType === 'generic' ? 'active' : ''}`}
                onClick={() => handleTemplateChange('generic')}
              >
                <span className="template-icon">📄</span>
                <span className="template-name">Generic</span>
              </button>
              <button
                type="button"
                className={`template-option ${templateType === 'vehicle' ? 'active' : ''}`}
                onClick={() => handleTemplateChange('vehicle')}
              >
                <span className="template-icon">🚗</span>
                <span className="template-name">Vehicle</span>
              </button>
              <button
                type="button"
                className={`template-option ${templateType === 'electronics' ? 'active' : ''}`}
                onClick={() => handleTemplateChange('electronics')}
              >
                <span className="template-icon">📱</span>
                <span className="template-name">Electronics</span>
              </button>
              <button
                type="button"
                className={`template-option ${templateType === 'equipment' ? 'active' : ''}`}
                onClick={() => handleTemplateChange('equipment')}
              >
                <span className="template-icon">🔧</span>
                <span className="template-name">Equipment</span>
              </button>
            </div>
          </div>
        )}

        {/* Blank Template CTA - only for unauthenticated users */}
        {!isAuthenticated && !isEditing && (
          <div className="blank-template-card">
            <div className="blank-template-content">
              <div className="blank-template-icon">📄</div>
              <div className="blank-template-text">
                <h3>Free Printable Bill of Sale</h3>
                <p>Download a blank bill of sale template (PDF) to print and fill out by hand</p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline blank-template-btn"
              onClick={handleDownloadBlankTemplate}
            >
              Download Blank Bill of Sale
            </button>
          </div>
        )}

        <AdBanner slot="bos-top" />

        <form onSubmit={handleSubmit} className="document-form">
          {/* Seller Information */}
          <div className="form-section">
            <h2 className="section-title">Seller Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.seller.name}
                  onChange={(e) => handleChange('seller', 'name', e.target.value)}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.seller.email}
                  onChange={(e) => handleChange('seller', 'email', e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.seller.phone}
                  onChange={(e) => handleChange('seller', 'phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.seller.address.street}
                  onChange={(e) => handleAddressChange('seller', 'street', e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.seller.address.city}
                  onChange={(e) => handleAddressChange('seller', 'city', e.target.value)}
                  placeholder="New York"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.seller.address.state}
                  onChange={(e) => handleAddressChange('seller', 'state', e.target.value)}
                  placeholder="NY"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.seller.address.zipCode}
                  onChange={(e) => handleAddressChange('seller', 'zipCode', e.target.value)}
                  placeholder="10001"
                />
              </div>
            </div>
          </div>

          {/* Buyer Information */}
          <div className="form-section">
            <h2 className="section-title">Buyer Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.buyer.name}
                  onChange={(e) => handleChange('buyer', 'name', e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.buyer.email}
                  onChange={(e) => handleChange('buyer', 'email', e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.buyer.phone}
                  onChange={(e) => handleChange('buyer', 'phone', e.target.value)}
                  placeholder="(555) 987-6543"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.buyer.address.street}
                  onChange={(e) => handleAddressChange('buyer', 'street', e.target.value)}
                  placeholder="456 Oak Ave"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.buyer.address.city}
                  onChange={(e) => handleAddressChange('buyer', 'city', e.target.value)}
                  placeholder="Los Angeles"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.buyer.address.state}
                  onChange={(e) => handleAddressChange('buyer', 'state', e.target.value)}
                  placeholder="CA"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.buyer.address.zipCode}
                  onChange={(e) => handleAddressChange('buyer', 'zipCode', e.target.value)}
                  placeholder="90001"
                />
              </div>
            </div>
          </div>

          {/* Item Details */}
          <div className="form-section">
            <h2 className="section-title">Item Details</h2>
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label className="form-label">Item Description *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.description}
                  onChange={(e) => handleChange('item', 'description', e.target.value)}
                  placeholder="e.g., 2020 Apple MacBook Pro 13-inch"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Make/Brand</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.make}
                  onChange={(e) => handleChange('item', 'make', e.target.value)}
                  placeholder="Apple"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Model</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.model}
                  onChange={(e) => handleChange('item', 'model', e.target.value)}
                  placeholder="MacBook Pro"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Year</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.year}
                  onChange={(e) => handleChange('item', 'year', e.target.value)}
                  placeholder="2020"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Serial Number</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.serialNumber}
                  onChange={(e) => handleChange('item', 'serialNumber', e.target.value)}
                  placeholder="ABC123XYZ"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.item.color}
                  onChange={(e) => handleChange('item', 'color', e.target.value)}
                  placeholder="Space Gray"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Condition</label>
                <select
                  className="form-select"
                  value={formData.item.condition}
                  onChange={(e) => handleChange('item', 'condition', e.target.value)}
                >
                  <option value="new">New</option>
                  <option value="like-new">Like New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Additional Details</label>
                <textarea
                  className="form-textarea"
                  value={formData.item.additionalDetails}
                  onChange={(e) => handleChange('item', 'additionalDetails', e.target.value)}
                  placeholder="Any additional details about the item..."
                  rows={3}
                />
              </div>

              {/* Vehicle-specific fields */}
              {templateType === 'vehicle' && (
                <>
                  <div className="form-group">
                    <label className="form-label">VIN (Vehicle Identification Number)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.item.vin}
                      onChange={(e) => handleChange('item', 'vin', e.target.value.toUpperCase())}
                      placeholder="1HGBH41JXMN109186"
                      maxLength={17}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Odometer Reading</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.item.odometer}
                      onChange={(e) => handleChange('item', 'odometer', e.target.value)}
                      placeholder="75,000 miles"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">License Plate #</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.item.licensePlate}
                      onChange={(e) => handleChange('item', 'licensePlate', e.target.value.toUpperCase())}
                      placeholder="ABC1234"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Title Status</label>
                    <select
                      className="form-select"
                      value={formData.item.titleStatus}
                      onChange={(e) => handleChange('item', 'titleStatus', e.target.value)}
                    >
                      <option value="clean">Clean Title</option>
                      <option value="salvage">Salvage Title</option>
                      <option value="rebuilt">Rebuilt Title</option>
                      <option value="lien">Lien on Title</option>
                      <option value="bonded">Bonded Title</option>
                    </select>
                  </div>
                </>
              )}

              {/* Electronics-specific fields */}
              {templateType === 'electronics' && (
                <>
                  <div className="form-group">
                    <label className="form-label">IMEI (for phones)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.item.imei}
                      onChange={(e) => handleChange('item', 'imei', e.target.value)}
                      placeholder="353456789012345"
                      maxLength={15}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Storage Capacity</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.item.storageCapacity}
                      onChange={(e) => handleChange('item', 'storageCapacity', e.target.value)}
                      placeholder="256GB"
                    />
                  </div>
                </>
              )}

              {/* Equipment-specific fields */}
              {templateType === 'equipment' && (
                <div className="form-group form-group-full">
                  <label className="form-label">Accessories Included</label>
                  <textarea
                    className="form-textarea"
                    value={formData.item.accessories}
                    onChange={(e) => handleChange('item', 'accessories', e.target.value)}
                    placeholder="List any accessories, attachments, or extras included with the equipment..."
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Transaction Details */}
          <div className="form-section">
            <h2 className="section-title">Transaction Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Sale Price ($) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.transaction.salePrice}
                  onChange={(e) => handleChange('transaction', 'salePrice', e.target.value)}
                  placeholder="500.00"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select
                  className="form-select"
                  value={formData.transaction.paymentMethod}
                  onChange={(e) => handleChange('transaction', 'paymentMethod', e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="bank-transfer">Bank Transfer</option>
                  <option value="credit-card">Credit Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sale Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.transaction.saleDate}
                  onChange={(e) => handleChange('transaction', 'saleDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sale Location</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.transaction.location}
                  onChange={(e) => handleChange('transaction', 'location', e.target.value)}
                  placeholder="City, State"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Platform</label>
                <select
                  className="form-select"
                  value={formData.platform}
                  onChange={(e) => setFormData(prev => ({ ...prev, platform: e.target.value }))}
                >
                  <option value="craigslist">Craigslist</option>
                  <option value="facebook-marketplace">Facebook Marketplace</option>
                  <option value="offerup">OfferUp</option>
                  <option value="letgo">Letgo</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="form-section">
            <h2 className="section-title">Terms & Conditions</h2>
            <div className="form-checkbox-group">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.terms.asIs}
                  onChange={(e) => handleChange('terms', 'asIs', e.target.checked)}
                />
                <span>Sold "As Is" - No warranty or guarantee</span>
              </label>
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.terms.warranty}
                  onChange={(e) => handleChange('terms', 'warranty', e.target.checked)}
                />
                <span>Include Warranty</span>
              </label>
            </div>
            {formData.terms.warranty && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label className="form-label">Warranty Details</label>
                <textarea
                  className="form-textarea"
                  value={formData.terms.warrantyDetails}
                  onChange={(e) => handleChange('terms', 'warrantyDetails', e.target.value)}
                  placeholder="Describe the warranty terms..."
                  rows={2}
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="form-section">
            <h2 className="section-title">Additional Notes</h2>
            <div className="form-group">
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about this sale..."
                rows={3}
              />
            </div>
          </div>

          {/* Signature Section - Premium Only */}
          {isAuthenticated && isPremium() && (
            <div className="form-section signature-section">
              <h2 className="section-title">Signatures</h2>
              <p className="section-description">
                Both parties sign below.
              </p>
              <div className="signatures-grid">
                <div className="signature-block">
                  <label className="form-label">Seller Signature</label>
                  <SignaturePad
                    onSave={setSellerSignature}
                    onClear={() => setSellerSignature(null)}
                  />
                </div>
                <div className="signature-block">
                  <label className="form-label">Buyer Signature</label>
                  <SignaturePad
                    onSave={setBuyerSignature}
                    onClear={() => setBuyerSignature(null)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="form-actions-bottom">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Generate & Download PDF'}
            </button>
            
            {/* Email copies option - Premium Only */}
            {isAuthenticated && isPremium() && (
              <label className="checkbox-label email-checkbox">
                <input
                  type="checkbox"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                />
                <span>📧 Send email copies to both parties</span>
              </label>
            )}
            
            {sendEmails && isPremium() && (!formData.seller.email || !formData.buyer.email) && (
              <p className="form-note warning">
                ⚠️ Please fill in email addresses for both seller and buyer above
              </p>
            )}
            
            {!isAuthenticated && (
              <p className="form-note">
                💡 <a href="/register">Sign up</a> to save your documents
              </p>
            )}
            {isAuthenticated && !isPremium() && (
              <p className="form-note">
                ⭐ <a href="/subscription">Upgrade to Premium</a> for e-signatures & document history
              </p>
            )}
          </div>
        </form>

        <AdBanner slot="bos-bottom" />
      </div>
    </div>
  );
};

export default BillOfSale;
